import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePurchases } from '../contexts/PurchasesContext';
import {
  FontText,
  SEOHead,
  Breadcrumbs,
  TableOfContents,
  BookCard,
  FAQSection,
  MarkdownText,
} from './common';
import { Header } from './Header';
import { localAnalytics } from '../utils/analytics';

interface BreadcrumbItem {
  name: string;
  url?: string;
}

interface TocItem {
  id: string;
  title: string;
}

interface BookCardData {
  title: string;
  hook: string;
  slug?: string;
}

interface SectionData {
  id: string;
  title: string;
  angle?: string;
  copy: string;
  bookCards: BookCardData[];
}

interface FAQItem {
  q: string;
  a: string;
}

interface HubData {
  meta: {
    title: string;
    metaDescription: string;
    canonical: string;
    openGraph: {
      og_title?: string;
      og_description?: string;
      og_url?: string;
      og_image?: string;
      og_image_alt?: string;
    };
    breadcrumb: string[];
  };
  intro: string;
  toc: TocItem[];
  sections: SectionData[];
  faq: FAQItem[];
  updatedOn?: string;
}

interface HubProps {
  data: HubData;
  hubName: string;
}

export const Hub: React.FC<HubProps> = ({ data, hubName }) => {
  const { theme } = useTheme();
  const { userId, isAuthenticated, isCampaign, isAnonymous } = useAuth();
  const { isUserPremium, coinBalance } = usePurchases();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    localAnalytics().logEvent('HubPageViewed', {
      screen: 'Hub',
      action: 'PageViewed',
      userId,
      hubName,
    });
  }, [hubName, userId]);

  const isLargeScreen = window.innerWidth > 900;

  const breadcrumbItems: BreadcrumbItem[] = data.meta.breadcrumb.map(
    (item, index) => ({
      name: item,
      url: index === 0 ? '/' : index === 1 ? '/hub' : undefined,
    })
  );

  const renderCopyWithHTML = (copy: string) => {
    return { __html: copy };
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
    },
    main: {
      padding: '20px 30px',
      maxWidth: 1200,
      margin: '0 auto',
    },
    intro: {
      color: theme.colors.text,
      lineHeight: '1.7',
      marginBottom: 40,
    },
    section: {
      marginBottom: 60,
      scrollMarginTop: 100,
    },
    sectionHeader: {
      marginBottom: 24,
    },
    sectionTitle: {
      color: theme.colors.text,
      marginBottom: 8,
    },
    sectionAngle: {
      color: theme.colors.textSecondary,
      fontStyle: 'italic',
      marginBottom: 16,
    },
    sectionCopy: {
      color: theme.colors.text,
      lineHeight: '1.6',
      marginBottom: 24,
    },
    bookGrid: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(${isLargeScreen ? '300px' : '280px'}, 1fr))`,
      gap: 20,
    },
    updatedBadge: {
      display: 'inline-block',
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      padding: '4px 12px',
      borderRadius: 16,
      border: `1px solid ${theme.colors.overlay}`,
      marginBottom: 20,
    },
  };

  return (
    <>
      <SEOHead
        title={data.meta.title}
        description={data.meta.metaDescription}
        canonical={data.meta.canonical}
        ogImage={data.meta.openGraph.og_image || '/img/favicon.png'}
        ogType="article"
      />
      <div style={styles.container}>
        <Header
          isPremium={isUserPremium}
          isAuthenticated={isAuthenticated}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          userCoins={coinBalance}
          menuOpen={menuOpen}
          onMenuToggle={() => setMenuOpen(!menuOpen)}
        />

        <main style={styles.main}>
          <Breadcrumbs items={breadcrumbItems} />

          {data.updatedOn && (
            <div style={styles.updatedBadge}>
              <FontText small>Updated {data.updatedOn}</FontText>
            </div>
          )}

          <MarkdownText style={styles.intro}>{data.intro}</MarkdownText>

          <TableOfContents items={data.toc} />

          {data.sections.map(section => (
            <section key={section.id} id={section.id} style={styles.section}>
              <div style={styles.sectionHeader}>
                <FontText h2 style={styles.sectionTitle}>
                  💕 {section.title}
                </FontText>
                {section.angle && (
                  <FontText normal style={styles.sectionAngle}>
                    {section.angle}
                  </FontText>
                )}
              </div>

              <MarkdownText style={styles.sectionCopy}>
                {section.copy}
              </MarkdownText>

              <div style={styles.bookGrid}>
                {section.bookCards.map((book, index) => (
                  <BookCard
                    key={index}
                    book={book}
                    source={`Hub-${hubName}-${section.id}`}
                  />
                ))}
              </div>
            </section>
          ))}

          <FAQSection faqs={data.faq} />
        </main>
      </div>
    </>
  );
};
